import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { marketDaysApi, packListsApi, salesRecordsApi, kegRecordsApi } from '../../services/api';
import './MarketDayDetail.css';

export default function MarketDayDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [marketDay, setMarketDay] = useState(null);
  const [records, setRecords] = useState([]);
  const [kegRecords, setKegRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creatingNext, setCreatingNext] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [activeTab, setActiveTab] = useState('AR');
  const [showAddKeg, setShowAddKeg] = useState(false);
  const [newKegForm, setNewKegForm] = useState({ flavor: '', start_weight: '' });
  const fileRef = useRef();

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const [mdData, recData, kegRecData] = await Promise.all([
        marketDaysApi.get(id),
        salesRecordsApi.getForMarketDay(id),
        kegRecordsApi.getForMarketDay(id),
      ]);
      setMarketDay(mdData.market_day);
      setRecords(recData.records || []);
      setKegRecords(kegRecData.keg_records || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  function handleLocalChange(recordId, field, value) {
    setRecords(rs => rs.map(r => r.id === recordId ? { ...r, [field]: value } : r));
  }

  async function handleBlur(recordId, field, value) {
    try {
      await salesRecordsApi.update({ id: recordId, [field]: value });
    } catch (e) {
      setError(e.message);
    }
  }

  function handleKegLocalChange(recordId, field, value) {
    setKegRecords(rs => rs.map(r => r.id === recordId ? { ...r, [field]: value } : r));
  }

  async function handleKegBlur(recordId, field, value) {
    try {
      await kegRecordsApi.update({ id: recordId, [field]: value });
    } catch (e) {
      setError(e.message);
    }
  }

  async function addKegRecord(e) {
    e.preventDefault();
    try {
      await kegRecordsApi.create({
        market_day_id: id,
        flavor: newKegForm.flavor,
        start_weight: newKegForm.start_weight,
        finish_weight: '',
        weight_used: '',
        notes: '',
      });
      await load();
      setShowAddKeg(false);
      setNewKegForm({ flavor: '', start_weight: '' });
    } catch (e) {
      setError(e.message);
    }
  }

  async function handlePhotoScan(e) {
    const file = e.target.files[0];
    if (!file) return;
    setScanning(true);
    setError('');
    try {
      const base64 = await fileToBase64(file);
      const result = await marketDaysApi.scanSheet({ image: base64, marketDayId: id, company: activeTab });
      if (result.records) {
        setRecords(rs => {
          const updated = [...rs];
          result.records.forEach(scanned => {
            const idx = updated.findIndex(r => r.id === scanned.id);
            if (idx >= 0) updated[idx] = { ...updated[idx], ...scanned };
          });
          return updated;
        });
      }
    } catch (e) {
      setError('Scan failed: ' + e.message);
    } finally {
      setScanning(false);
      fileRef.current.value = '';
    }
  }

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  function handlePrintMarketSheet() {
    const arRows = records.filter(r => r.company === 'AR');
    const ccRows = records.filter(r => r.company === 'CC');

    const AR_CATEGORIES = [
      { key: 'ar_frozen', label: 'Frozen & Prepared Foods' },
      { key: 'ar_chicken', label: 'Chicken' },
      { key: 'ar_pork', label: 'Pork' },
      { key: 'ar_refrigerated', label: 'Refrigerated' },
    ];
    const CC_CATEGORIES = [
      { key: 'cc_veg', label: 'Fermented Veg' },
      { key: 'cc_brine', label: 'Brines' },
      { key: 'cc_bev', label: 'Kombucha & Beverages' },
      { key: 'cc_keg', label: 'Kegs' },
    ];

    const categorySection = (rows, cats) => cats.map(cat => {
      const catRows = rows.filter(r => r.category === cat.key);
      if (catRows.length === 0) return '';
      return `
        <tr><td colspan="5" style="background:#e8e8e8;font-weight:bold;padding:6px 8px;">${cat.label}</td></tr>
        ${catRows.map(r => `
        <tr>
          <td>${r.product_name || ''}</td>
          <td style="min-width:60px">${r.qty_packed || ''}</td>
          <td style="min-width:60px"></td>
          <td style="min-width:60px"></td>
          <td style="min-width:80px"></td>
        </tr>`).join('')}`;
    }).join('');

    const html = `
      <html><head><title>${marketDay.market_name} Market Sheet</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; }
        h2 { margin-bottom: 4px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
        th { background: #2c3e50; color: white; font-weight: bold; }
        td:nth-child(3), td:nth-child(4), td:nth-child(5) { min-width: 60px; }
      </style></head>
      <body>
        ${arRows.length > 0 ? `
        <div style="page-break-after: ${ccRows.length > 0 ? 'always' : 'avoid'};">
          <h2>${marketDay.market_name} — Apple Ridge Farm</h2>
          <p>${marketDay.date} &nbsp;|&nbsp; Weather: _________________ &nbsp; Temp: ______°F &nbsp; Staff: _________________</p>
          <table>
            <thead><tr><th>Product</th><th>Start</th><th>Finish</th><th>Request Next Week</th><th>Notes</th></tr></thead>
            <tbody>${categorySection(arRows, AR_CATEGORIES)}</tbody>
          </table>
        </div>` : ''}
        ${ccRows.length > 0 ? `
        <div>
          <h2>${marketDay.market_name} — Collective Culture</h2>
          <p>${marketDay.date} &nbsp;|&nbsp; Weather: _________________ &nbsp; Temp: ______°F &nbsp; Staff: _________________</p>
          <table>
            <thead><tr><th>Product</th><th>Start</th><th>Finish</th><th>Request Next Week</th><th>Notes</th></tr></thead>
            <tbody>${categorySection(ccRows, CC_CATEGORIES)}</tbody>
          </table>
        </div>` : ''}
      </body></html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.print();
  }

  async function handleCreateNextWeekPackList() {
    if (!marketDay?.market_id) return;

    // Calculate next occurrence of same day of week
    const currentDate = new Date(marketDay.date);
    const dayOfWeek = currentDate.getDay();
    const nextDate = new Date(currentDate);
    nextDate.setDate(currentDate.getDate() + 7);
    const suggested = nextDate.toISOString().slice(0, 10);

    const chosenDate = window.prompt(
      'Create next pack list for ' + marketDay.market_name + '\nEnter the market date (YYYY-MM-DD):',
      suggested
    );
    if (!chosenDate) return;

    setCreatingNext(true);
    try {
      // Determine type from day of week
      const d = new Date(chosenDate);
      const dow = d.getDay();
      const type = dow === 0 ? 'AR_SUN' : dow === 6 ? 'AR_SAT' : 'CC';

      const result = await packListsApi.create({
        type: marketDay.market_type || type,
        week_date: chosenDate,
        market_id: marketDay.market_id,
        market_name: marketDay.market_name,
      });
      alert('✓ Pack list created for ' + marketDay.market_name + ' on ' + chosenDate);
      navigate('/ops/pack-lists/' + result.id);
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setCreatingNext(false);
    }
  }

  async function markComplete() {
    try {
      await marketDaysApi.update({ id, status: 'complete' });
      setMarketDay(md => ({ ...md, status: 'complete' }));
    } catch (e) {
      setError(e.message);
    }
  }

  const CAT_ORDER = ['ar_frozen', 'ar_chicken', 'ar_pork', 'ar_refrigerated', 'cc_veg', 'cc_brine', 'cc_bev', 'cc_keg'];
  const sortByCat = (a, b) => {
    const ai = CAT_ORDER.indexOf(a.category);
    const bi = CAT_ORDER.indexOf(b.category);
    if (ai !== bi) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    return (a.product_name || '').localeCompare(b.product_name || '');
  };
  const arRecords = records.filter(r => r.company === 'AR').sort(sortByCat);
  const ccRecords = records.filter(r => r.company === 'CC').sort(sortByCat);
  const activeRecords = activeTab === 'AR' ? arRecords : activeTab === 'CC' ? ccRecords : [];

  if (loading) return <div className="loading-state" style={{padding: 48}}>Loading...</div>;
  if (!marketDay) return <div style={{padding: 48}}>Market day not found.</div>;

  return (
    <div className="page">
      <div className="detail-header">
        <button className="back-btn" onClick={() => navigate('/ops/market-days', { state: { refresh: Date.now() } })}>Back</button>
        <div className="detail-header-info">
          <h1>{marketDay.market_name}</h1>
          <p>{marketDay.date} · {marketDay.weather_condition} {marketDay.temperature ? `· ${marketDay.temperature}F` : ''}</p>
        </div>
        <div className="detail-header-actions">
          <button className="btn-secondary" onClick={handlePrintMarketSheet}>🖨 Print Market Sheet</button>
          <button className="btn-secondary" onClick={handleCreateNextWeekPackList} disabled={creatingNext}>
            {creatingNext ? 'Creating...' : '📅 Next Week\'s Pack List'}
          </button>
          {marketDay.status !== 'complete' && (
            <button className="btn-primary" onClick={markComplete}>Mark Complete</button>
          )}
          {marketDay.status === 'complete' && (
            <span className="pill pill-green">Complete</span>
          )}
        </div>
      </div>

      <div className="print-market-header">
        <h2>{marketDay.market_name} — Market Sheet</h2>
        <p>{marketDay.date} &nbsp;|&nbsp; {marketDay.weather_condition} {marketDay.temperature ? `· ${marketDay.temperature}°F` : ''}</p>
        <hr/>
      </div>
      <div className="page-body">
        {error && <div className="error-banner">{error}</div>}

        <div className="tab-bar">
          {['AR', 'CC', 'Kegs'].map(tab => (
            <button
              key={tab}
              className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'AR' ? 'Apple Ridge Farm' : tab === 'CC' ? 'Collective Culture' : 'Kegs'}
              {tab !== 'Kegs' && (
                <span className="tab-count">
                  {tab === 'AR' ? arRecords.length : ccRecords.length} items
                </span>
              )}
              {tab === 'Kegs' && <span className="tab-count">{kegRecords.length} kegs</span>}
            </button>
          ))}
        </div>

        {activeTab === 'Kegs' ? (
          <div>
            <div className="scan-bar">
              <button className="btn-scan" onClick={() => setShowAddKeg(true)}>+ Add Keg</button>
              <span className="scan-hint">Record start and finish weights for each keg at this market</span>
            </div>
            {kegRecords.length === 0 ? (
              <div className="empty-state">No kegs logged for this market day yet.</div>
            ) : (
              <div className="records-table-wrap">
                <table className="data-table records-table">
                  <thead>
                    <tr>
                      <th>Flavor</th>
                      <th>Start Weight (lbs)</th>
                      <th>Finish Weight (lbs)</th>
                      <th>Used (lbs)</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kegRecords.map(r => {
                      const used = r.start_weight && r.finish_weight
                        ? (parseFloat(r.start_weight) - parseFloat(r.finish_weight)).toFixed(1)
                        : '-';
                      return (
                        <tr key={r.id}>
                          <td className="td-name">{r.flavor}</td>
                          <td>
                            <input type="number" step="0.1" className="qty-input"
                              value={r.start_weight || ''}
                              onChange={e => handleKegLocalChange(r.id, 'start_weight', e.target.value)}
                              onBlur={e => handleKegBlur(r.id, 'start_weight', e.target.value)}
                            />
                          </td>
                          <td>
                            <input type="number" step="0.1" className="qty-input"
                              value={r.finish_weight || ''}
                              onChange={e => handleKegLocalChange(r.id, 'finish_weight', e.target.value)}
                              onBlur={e => handleKegBlur(r.id, 'finish_weight', e.target.value)}
                            />
                          </td>
                          <td className="td-price">{used}</td>
                          <td>
                            <input type="text" className="notes-input"
                              value={r.notes || ''}
                              onChange={e => handleKegLocalChange(r.id, 'notes', e.target.value)}
                              onBlur={e => handleKegBlur(r.id, 'notes', e.target.value)}
                              placeholder="Notes..."
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {showAddKeg && (
              <div className="modal-overlay" onClick={() => setShowAddKeg(false)}>
                <div className="modal" onClick={e => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2>Add Keg to Market Day</h2>
                    <button className="modal-close" onClick={() => setShowAddKeg(false)}>x</button>
                  </div>
                  <form onSubmit={addKegRecord} className="modal-form">
                    <div className="field">
                    <div className="field">
                      <label>Flavor *</label>
                      <input type="text" value={newKegForm.flavor}
                        onChange={e => setNewKegForm(f => ({ ...f, flavor: e.target.value }))}
                        placeholder="e.g. Hibiscus Kombucha" required />
                    </div>
                    </div>
                    <div className="field">
                      <label>Starting Weight (lbs) *</label>
                      <input type="number" step="0.1" value={newKegForm.start_weight}
                        onChange={e => setNewKegForm(f => ({ ...f, start_weight: e.target.value }))}
                        placeholder="55.0" required />
                    </div>
                    <div className="modal-actions">
                      <button type="button" className="btn-secondary" onClick={() => setShowAddKeg(false)}>Cancel</button>
                      <button type="submit" className="btn-primary">Add Keg</button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="scan-bar">
              <input type="file" accept="image/*" capture="environment" ref={fileRef}
                style={{ display: 'none' }} onChange={handlePhotoScan} />
              <button className="btn-scan" onClick={() => fileRef.current.click()} disabled={scanning}>
                {scanning ? 'Scanning...' : `Scan ${activeTab} Sheet`}
              </button>
              <span className="scan-hint">Take a photo of the {activeTab === 'AR' ? 'Apple Ridge' : 'Collective Culture'} market sheet to auto-fill</span>
            </div>
            {activeRecords.length === 0 ? (
              <div className="empty-state">No products assigned to this market.</div>
            ) : (
              <div className="records-table-wrap">
                <table className="data-table records-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Start</th>
                      <th>Finish</th>
                      <th>Request Next Week</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const CAT_LABELS = {
                        'ar_frozen': 'Frozen & Prepared',
                        'ar_chicken': 'Chicken',
                        'ar_pork': 'Pork',
                        'ar_refrigerated': 'Refrigerated',
                        'cc_veg': 'Fermented Veg',
                        'cc_brine': 'Brines',
                        'cc_bev': 'Kombucha & Beverages',
                        'cc_keg': 'Kegs',
                      };
                      let lastCat = null;
                      return activeRecords.flatMap(r => {
                        const rows = [];
                        if (r.category !== lastCat) {
                          lastCat = r.category;
                          rows.push(
                            <tr key={`cat-${r.category}-${r.id}`} className="category-header-row">
                              <td colSpan="5" style={{background:'#e8f0fe', fontWeight:'700', fontSize:'0.78rem', color:'#2c3e50', padding:'4px 8px', textTransform:'uppercase', letterSpacing:'0.05em'}}>
                                {CAT_LABELS[r.category] || r.category}
                              </td>
                            </tr>
                          );
                        }
                        rows.push(
                        <tr key={r.id}>
                        <td className="td-name">{r.product_name}</td>
                        <td>
                          <input type="number" className="qty-input" value={r.qty_packed || ''}
                            onChange={e => handleLocalChange(r.id, 'qty_packed', e.target.value)}
                            onBlur={e => handleBlur(r.id, 'qty_packed', e.target.value)} min="0" />
                        </td>
                        <td>
                          <input type="number" className="qty-input" value={r.qty_sold || ''}
                            onChange={e => handleLocalChange(r.id, 'qty_sold', e.target.value)}
                            onBlur={e => handleBlur(r.id, 'qty_sold', e.target.value)} min="0" />
                        </td>
                        <td>
                          <input type="number" className="qty-input" value={r.qty_requested_next_week || ''}
                            onChange={e => handleLocalChange(r.id, 'qty_requested_next_week', e.target.value)}
                            onBlur={e => handleBlur(r.id, 'qty_requested_next_week', e.target.value)} min="0" />
                        </td>
                        <td>
                          <input type="text" className="notes-input" value={r.notes || ''}
                            onChange={e => handleLocalChange(r.id, 'notes', e.target.value)}
                            onBlur={e => handleBlur(r.id, 'notes', e.target.value)} placeholder="Notes..." />
                        </td>
                      </tr>
                        );
                        return rows;
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
